"use client";

import { useState, useEffect, useRef } from "react";
import { Task, TaskType } from "../../api/referralApi";
import { ChevronRightIcon, CheckIcon, ClockIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
import { toast } from "react-toastify";

// Map task identifiers to icon paths
const getTaskIcon = (taskId: string) => {
  const iconClass = "h-8 w-8";
  
  switch (taskId) {
    case "google_rate":
      return (
        <Image
          src="/icons/referral/google.svg"
          alt="Google"
          width={32}
          height={32}
          className={iconClass}
        />
      );
    case "trustpilot_rate":
      return (
        <Image
          src="/icons/referral/trustpilot.svg"
          alt="Trustpilot"
          width={32}
          height={32}
          className={iconClass}
        />
      );
    case "discord_join":
      return (
        <Image
          src="/icons/referral/discord.svg"
          alt="Discord"
          width={32}
          height={32}
          className={iconClass}
        />
      );
    case "twitter_follow":
      return (
        <Image
          src="/icons/referral/x.svg"
          alt="Twitter/X"
          width={32}
          height={32}
          className={iconClass}
        />
      );
    case "linkedin_post":
      return (
        <Image
          src="/icons/referral/linkedin.svg"
          alt="LinkedIn"
          width={32}
          height={32}
          className={iconClass}
        />
      );
    case "extension_rate":
      return (
        <Image
          src="/icons/referral/google-chrome.svg"
          alt="Chrome Extension"
          width={32}
          height={32}
          className={iconClass}
        />
      );
    default:
      return (
        <div className={`${iconClass} rounded-full bg-transparent p-1`}>
          <span className="text-sm font-bold">🟩</span>
        </div>
      );
  }
};

// Map task identifiers to external URLs
const getTaskUrl = (taskId: string): string | null => {
  const urlMap: Record<string, string> = {
    trustpilot_rate: "https://www.trustpilot.com/evaluate/resumemate.io",
    google_rate: "https://g.page/r/Cd4Sb8MRbPHEEBM/review",
    discord_join: "https://discord.gg/kWcggqvg",
    extension_rate: "https://chromewebstore.google.com/detail/ccjoemjdjbdjpbgpoceddhfmbllonbgi",
    twitter_follow: "https://x.com/resume_mate",
  };
  
  return urlMap[taskId] || null;
};

// Format time remaining
const formatTimeRemaining = (milliseconds: number): string => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  } else {
    return `${seconds}s`;
  }
};

interface TaskCardProps {
  task: Task;
  taskType: TaskType;
  onInitiateTask: (task: Task) => void;
  onClaimReward: (task: Task) => void;
  onBalanceRefresh?: () => void;
}

const TaskCard = ({ task, taskType, onInitiateTask, onClaimReward, onBalanceRefresh }: TaskCardProps) => {
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [isTimerExpired, setIsTimerExpired] = useState(false);
  const hasShownToast = useRef(false);
  
  // Track if task has verificationDueAt (means it's been initiated)
  const hasVerificationTime = task.verificationDueAt !== null;

  // Generate unique key for this task completion
  const getToastKey = () => {
    if (!task.verificationDueAt) return null;
    return `toast_shown_${task.taskId}_${task.verificationDueAt}`;
  };

  // Check if toast was already shown for this specific task completion
  const wasToastAlreadyShown = () => {
    const key = getToastKey();
    if (!key) return false;
    
    try {
      const shownToasts = localStorage.getItem('shown_task_toasts');
      if (!shownToasts) return false;
      
      const toastMap = JSON.parse(shownToasts) as Record<string, number>;
      
      // Check if this toast was shown (stored with timestamp)
      if (toastMap[key]) {
        // Clean up old toast records (older than 7 days)
        const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
        if (toastMap[key] < sevenDaysAgo) {
          delete toastMap[key];
          localStorage.setItem('shown_task_toasts', JSON.stringify(toastMap));
          return false;
        }
        return true;
      }
      
      return false;
    } catch (error) {
      // console.warn('Error checking toast status:', error);
      return false;
    }
  };

  // Mark toast as shown for this task completion
  const markToastAsShown = () => {
    const key = getToastKey();
    if (!key) return;
    
    try {
      const shownToasts = localStorage.getItem('shown_task_toasts');
      const toastMap = shownToasts ? JSON.parse(shownToasts) : {};
      
      toastMap[key] = Date.now();
      localStorage.setItem('shown_task_toasts', JSON.stringify(toastMap));
    } catch (error) {
      console.warn('Error marking toast as shown:', error);
    }
  };

  useEffect(() => {
    // ONLY use verificationDueAt to determine timer state
    if (hasVerificationTime && task.verificationDueAt) {
      const updateTimer = () => {
        const now = new Date().getTime();
        const dueTime = new Date(task.verificationDueAt!).getTime();
        const remaining = dueTime - now;
        
        if (remaining <= 0) {
          setTimeRemaining(0);
          setIsTimerExpired(true);
        } else {
          setTimeRemaining(remaining);
          setIsTimerExpired(false);
        }
      };
      
      // Update immediately
      updateTimer();
      
      // Update every second
      const interval = setInterval(updateTimer, 1000);
      
      return () => clearInterval(interval);
    } else {
      setTimeRemaining(null);
      setIsTimerExpired(false);
    }
  }, [hasVerificationTime, task.verificationDueAt]);
  
  // Show toast notification when timer expires (only once per task completion)
  useEffect(() => {
    if (isTimerExpired && !hasShownToast.current && !wasToastAlreadyShown()) {
      hasShownToast.current = true;
      const rewardAmount = taskType.minRewardValue === taskType.maxRewardValue 
        ? taskType.minRewardValue 
        : `${taskType.minRewardValue}-${taskType.maxRewardValue}`;
      toast.success(`🎉 Task verified! You earned ${rewardAmount} coins!`);
      
      // Mark this toast as shown in localStorage
      markToastAsShown();
      
      // Refresh balance when timer expires
      if (onBalanceRefresh) {
        onBalanceRefresh();
      }
    }
  }, [isTimerExpired, taskType.minRewardValue, taskType.maxRewardValue, onBalanceRefresh]);

  const getRewardText = () => {
    if (taskType.minRewardValue === taskType.maxRewardValue) {
      return `${taskType.minRewardValue} Coins`;
    }
    return `${taskType.minRewardValue}-${taskType.maxRewardValue} Coins`;
  };

  const handleCardClick = () => {
    // Only clickable if task has NOT been initiated yet (no verificationDueAt)
    if (!hasVerificationTime) {
      // Open URL in new tab if available (except LinkedIn)
      const url = getTaskUrl(task.taskId);
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
      
      // Call initiate task handler - this will call claim API and fetch tasks
      onInitiateTask(task);
    }
    // Once timer is running or expired, button is not clickable
  };

  const getButtonText = () => {
    // Determine button text ONLY based on verificationDueAt and timer
    // IGNORE status field completely
    if (hasVerificationTime) {
      if (isTimerExpired) {
        return "Claimed ✓";
      } else {
        return "In Progress";
      }
    } else {
      return "Claim Reward";
    }
  };

  const getButtonColor = () => {
    // Determine button color ONLY based on verificationDueAt and timer
    // IGNORE status field completely
    if (hasVerificationTime) {
      if (isTimerExpired) {
        return "bg-gray-400 text-white cursor-not-allowed";
      } else {
        return "bg-yellow-500 text-white cursor-not-allowed";
      }
    } else {
      return "bg-[#52a1ff] hover:bg-blue-700 text-white";
    }
  };

  // Only clickable if task has not been initiated yet
  const isClickable = !hasVerificationTime;

  const getButtonIcon = () => {
    // Determine icon ONLY based on verificationDueAt and timer
    // IGNORE status field completely
    if (hasVerificationTime) {
      if (isTimerExpired) {
        return <CheckIcon className="h-5 w-5" />;
      } else {
        return <ClockIcon className="h-5 w-5" />;
      }
    } else {
      return <ChevronRightIcon className="h-5 w-5" />;
    }
  };

  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
      {/* Content section */}
      <div className="flex-grow">
        {/* Header with icon and title */}
        <div className="mb-4 flex items-center gap-3">
          {getTaskIcon(task.taskId)}
          <h3 className="text-lg font-semibold text-gray-900">
            {taskType.displayName}
          </h3>
        </div>

        {/* Reward amounts */}
        <div className="mb-2">
          <p className="text-xl font-bold text-gray-900">
            {getRewardText()}
          </p>
        </div>

        {/* Timer - only visible when verificationDueAt exists and timer not expired */}
        {hasVerificationTime && timeRemaining !== null && !isTimerExpired && (
          <div className="mb-4 flex items-center gap-2 rounded-lg bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
            <ClockIcon className="h-4 w-4" />
            <span className="font-medium">
              Verification in: {formatTimeRemaining(timeRemaining)}
            </span>
          </div>
        )}

        {/* Timer completed - show claimed status */}
        {hasVerificationTime && isTimerExpired && (
          <div className="mb-4 flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800">
            <CheckIcon className="h-4 w-4" />
            <span className="font-medium">Verification complete!</span>
          </div>
        )}
      </div>

      {/* Action button - always at bottom */}
      <button
        onClick={handleCardClick}
        disabled={!isClickable}
        className={`mt-4 flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 font-medium transition-colors ${getButtonColor()}`}
      >
        {getButtonIcon()}
        {getButtonText()}
      </button>
    </div>
  );
};

export default TaskCard;